import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import SmartPagination from '@/components/SmartPagination';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Search, RefreshCw, Info, AlertTriangle, XCircle, Flame, Skull, Filter, Clock, User, FileText, Tag } from 'lucide-react';

const LEVELS = [
  { value: 1, label: 'Information', icon: Info, color: 'bg-blue-50 text-blue-700 border-blue-200', dot: 'bg-blue-500' },
  { value: 2, label: 'Warning', icon: AlertTriangle, color: 'bg-amber-50 text-amber-700 border-amber-200', dot: 'bg-amber-500' },
  { value: 3, label: 'Error', icon: XCircle, color: 'bg-red-50 text-red-700 border-red-200', dot: 'bg-red-500' },
  { value: 4, label: 'Critical', icon: Flame, color: 'bg-orange-50 text-orange-800 border-orange-300', dot: 'bg-orange-600' },
  { value: 5, label: 'Fatal', icon: Skull, color: 'bg-rose-100 text-rose-900 border-rose-400', dot: 'bg-rose-700' },
];

const getLevelConfig = (level) => LEVELS.find(l => l.value === level) || LEVELS[0];

const formatDateTime = (dateStr) => {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  return d.toLocaleDateString('tr-TR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit'
  });
};

const LogsTab = () => {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState({});
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState('');
  const [levelFilter, setLevelFilter] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [selectedLog, setSelectedLog] = useState(null);
  const pageSize = 30;

  const fetchLogs = useCallback(async (isRefresh) => {
    try {
      if (isRefresh) setRefreshing(true); else setLoading(true);
      const params = { page, limit: pageSize };
      if (levelFilter) params.level = levelFilter;
      if (categoryFilter) params.category = categoryFilter;
      if (search) params.search = search;

      const res = await api.get('/admin/system-logs', { params });
      setLogs(res.data.logs || []);
      setTotalPages(res.data.total_pages || 1);
      setTotal(res.data.total || 0);
    } catch (err) {
      toast.error('Loglar yuklenemedi');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [page, levelFilter, categoryFilter, search]);

  const fetchMeta = async () => {
    try {
      const [statsRes, catRes] = await Promise.allSettled([
        api.get('/admin/system-logs/stats'),
        api.get('/admin/system-logs/categories'),
      ]);
      if (statsRes.status === 'fulfilled') setStats(statsRes.value.data.stats || {});
      if (catRes.status === 'fulfilled') setCategories(catRes.value.data || []);
    } catch {}
  };

  useEffect(() => { fetchLogs(false); }, [fetchLogs]);
  useEffect(() => { fetchMeta(); }, []);

  const handleSearch = (val) => {
    setSearch(val);
    setPage(1);
  };

  const handleLevelClick = (level) => {
    setLevelFilter(prev => prev === level ? null : level);
    setPage(1);
  };

  return (
    <div className="space-y-4" data-testid="logs-tab">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        {LEVELS.map(lvl => {
          const count = stats[lvl.label] || 0;
          const isActive = levelFilter === lvl.value;
          const Icon = lvl.icon;
          return (
            <button
              key={lvl.value}
              onClick={() => handleLevelClick(lvl.value)}
              className={`flex items-center gap-2 p-3 rounded-lg border transition-all text-left ${
                isActive ? `${lvl.color} ring-2 ring-offset-1 ring-current` : 'bg-white border-gray-200 hover:border-gray-300'
              }`}
              data-testid={`log-level-${lvl.value}`}
            >
              <Icon className="w-4 h-4 shrink-0" />
              <div className="min-w-0">
                <p className="text-xs font-medium truncate">{lvl.label}</p>
                <p className="text-lg font-bold leading-tight">{count}</p>
              </div>
            </button>
          );
        })}
      </div>

      {/* Filters */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Sistem Loglari ({total})
            </CardTitle>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                <Input
                  placeholder="Ara..."
                  value={search}
                  onChange={e => handleSearch(e.target.value)}
                  className="pl-8 h-8 w-48 text-sm"
                  data-testid="log-search-input"
                />
              </div>
              <select
                value={categoryFilter}
                onChange={e => { setCategoryFilter(e.target.value); setPage(1); }}
                className="h-8 text-sm border rounded-md px-2 bg-white"
                data-testid="log-category-filter"
              >
                <option value="">Tum Kategoriler</option>
                {categories.map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
              <Button size="sm" variant="outline" onClick={() => { fetchLogs(true); fetchMeta(); }} className="h-8" data-testid="log-refresh-btn">
                <RefreshCw className={`w-3.5 h-3.5 mr-1 ${refreshing ? 'animate-spin' : ''}`} />
                Yenile
              </Button>
              {(levelFilter || categoryFilter || search) && (
                <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => { setLevelFilter(null); setCategoryFilter(''); setSearch(''); setPage(1); }}>
                  <Filter className="w-3 h-3 mr-1" /> Temizle
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-300 border-t-gray-800" />
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead className="w-[90px] font-semibold text-xs">Seviye</TableHead>
                    <TableHead className="w-[150px] font-semibold text-xs">Tarih/Saat</TableHead>
                    <TableHead className="w-[100px] font-semibold text-xs">Kategori</TableHead>
                    <TableHead className="font-semibold text-xs">Mesaj</TableHead>
                    <TableHead className="w-[140px] font-semibold text-xs">Kullanici</TableHead>
                    <TableHead className="w-[80px] font-semibold text-xs">Kaynak</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-12 text-gray-400">
                        {search || levelFilter || categoryFilter ? 'Filtre sonucu bulunamadi' : 'Henuz log kaydedilmemis'}
                      </TableCell>
                    </TableRow>
                  ) : logs.map((log, idx) => {
                    const lvl = getLevelConfig(log.level);
                    const Icon = lvl.icon;
                    return (
                      <TableRow
                        key={idx}
                        className="cursor-pointer hover:bg-gray-50 transition-colors"
                        onClick={() => setSelectedLog(log)}
                        data-testid={`log-row-${idx}`}
                      >
                        <TableCell>
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${lvl.color}`}>
                            <Icon className="w-3 h-3" />
                            {lvl.label}
                          </span>
                        </TableCell>
                        <TableCell className="text-xs text-gray-600 font-mono">
                          {formatDateTime(log.created_at)}
                        </TableCell>
                        <TableCell>
                          <span className="text-xs bg-gray-100 text-gray-700 px-1.5 py-0.5 rounded font-medium">
                            {log.category || '-'}
                          </span>
                        </TableCell>
                        <TableCell className="text-sm text-gray-800 max-w-xs truncate">
                          {log.message}
                        </TableCell>
                        <TableCell className="text-xs text-gray-500 truncate max-w-[130px]">
                          {log.user_email || log.user_id?.slice(0, 10) || '-'}
                        </TableCell>
                        <TableCell className="text-xs text-gray-400">
                          {log.source || '-'}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              {totalPages > 1 && (
                <div className="p-3 border-t">
                  <SmartPagination currentPage={page} totalPages={totalPages} onPageChange={setPage} pageSize={pageSize} />
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Log Detail Modal */}
      <Dialog open={!!selectedLog} onOpenChange={() => setSelectedLog(null)}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto" data-testid="log-detail-modal">
          {selectedLog && (() => {
            const lvl = getLevelConfig(selectedLog.level);
            const Icon = lvl.icon;
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border ${lvl.color}`}>
                      <Icon className="w-3.5 h-3.5" />
                      {lvl.label}
                    </span>
                    <span className="text-sm text-gray-500 font-normal">{selectedLog.category}</span>
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-2">
                  {/* Message */}
                  <div>
                    <p className="text-xs text-gray-400 mb-1 font-medium flex items-center gap-1"><FileText className="w-3 h-3" /> Mesaj</p>
                    <p className="text-sm font-medium text-gray-900 bg-gray-50 p-3 rounded-lg">{selectedLog.message}</p>
                  </div>

                  {/* Detail */}
                  {selectedLog.detail && (
                    <div>
                      <p className="text-xs text-gray-400 mb-1 font-medium">Detay</p>
                      <pre className="text-xs text-gray-700 bg-gray-900 text-gray-200 p-3 rounded-lg overflow-x-auto whitespace-pre-wrap font-mono max-h-60">
                        {selectedLog.detail}
                      </pre>
                    </div>
                  )}

                  <Separator />

                  {/* Meta Info */}
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-xs text-gray-400 flex items-center gap-1"><Clock className="w-3 h-3" /> Tarih/Saat</p>
                      <p className="font-mono text-xs mt-0.5">{formatDateTime(selectedLog.created_at)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 flex items-center gap-1"><Tag className="w-3 h-3" /> Kategori</p>
                      <p className="mt-0.5"><span className="bg-gray-100 px-2 py-0.5 rounded text-xs font-medium">{selectedLog.category}</span></p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 flex items-center gap-1"><User className="w-3 h-3" /> Kullanici</p>
                      <p className="text-xs mt-0.5">{selectedLog.user_email || selectedLog.user_id || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400">Kaynak</p>
                      <p className="text-xs mt-0.5">{selectedLog.source || '-'}</p>
                    </div>
                  </div>

                  {/* Metadata */}
                  {selectedLog.metadata && Object.keys(selectedLog.metadata).length > 0 && (
                    <div>
                      <p className="text-xs text-gray-400 mb-1 font-medium">Ek Veri</p>
                      <pre className="text-xs bg-gray-50 p-3 rounded-lg overflow-x-auto whitespace-pre-wrap font-mono">
                        {JSON.stringify(selectedLog.metadata, null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LogsTab;
